import React, { useState, useEffect } from 'react';
import { getAdminToken } from './AdminGate';

/**
 * Hidden admin page for creating/managing events.
 * Accessible only via direct URL: /admin/events
 * No link to this page exists anywhere on the public site.
 */
export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    title: '', description: '', game: '', date: '', time: '',
    host: 'The Country Club', maxPlayers: '', discordLink: '', rules: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [confirmingId, setConfirmingId] = useState(null);

  const fetchEvents = () => {
    fetch('/api/events', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => { setEvents(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const url = editingId ? `/api/events/${editingId}` : '/api/events';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({
          ...form,
          maxPlayers: form.maxPlayers ? parseInt(form.maxPlayers) : 0,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setMessage(editingId ? '✅ Event updated!' : '✅ Event created!');
        setForm(emptyForm);
        setEditingId(null);
        fetchEvents();
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleEdit = (event) => {
    setEditingId(event.id);
    setForm({
      title: event.title || '',
      description: event.description || '',
      game: event.game || '',
      date: event.date || '',
      time: event.time || '',
      host: event.host || 'The Country Club',
      maxPlayers: event.maxPlayers ? String(event.maxPlayers) : '',
      discordLink: event.discordLink || '',
      rules: event.rules || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const token = getAdminToken();
    if (!token) {
      alert('Admin session expired. Please refresh the page and re-enter the admin password.');
      return;
    }

    // Optimistically remove from state immediately so UI updates instantly
    const previousEvents = [...events];
    setEvents(prev => prev.filter(e => e.id !== id));
    setConfirmingId(null);

    try {
      const res = await fetch(`/api/events/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setMessage('🗑️ Event deleted');
        fetchEvents();
        setTimeout(() => setMessage(''), 3000);
      } else {
        // Rollback state if server returned an error
        setEvents(previousEvents);
        alert(`Failed to delete event: ${data.error || res.statusText || 'Unknown error'}`);
      }
    } catch (err) {
      // Rollback state on network error
      setEvents(previousEvents);
      alert(`Network error while deleting event: ${err.message}`);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const inputClass = "w-full px-3 py-2.5 rounded-lg text-sm font-mono bg-white/[0.04] border border-white/[0.08] text-white/80 placeholder-white/20 focus:outline-none focus:border-blue-500/40 transition-colors";
  const labelClass = "block text-[10px] font-mono text-white/30 uppercase tracking-wider mb-1.5";

  return (
    <section className="px-4 md:px-6 py-8 md:py-12">
      <div className="max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="mb-8 p-4 rounded-lg bg-amber-500/5 border border-amber-500/15">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-amber-400/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <h2 className="text-sm font-bold text-amber-400/80 font-mono">Admin: Event Management</h2>
          </div>
          <p className="text-xs text-amber-400/40 font-mono">This page is not linked anywhere on the public site</p>
        </div>

        {/* Message */}
        {message && (
          <div className="mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.08] text-sm font-mono text-white/70">
            {message}
          </div>
        )}

        {/* Create/Edit Form */}
        <form onSubmit={handleSubmit} className="mb-10 p-5 rounded-lg bg-white/[0.02] border border-white/[0.06]">
          <h3 className="text-sm font-bold text-white/70 font-mono mb-5">
            {editingId ? '✏️ Edit Event' : '➕ Create New Event'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="md:col-span-2">
              <label className={labelClass}>Event Title *</label>
              <input name="title" value={form.title} onChange={handleChange} placeholder="SOCOM II Friday Night Wars" className={inputClass} required />
            </div>

            {/* Game */}
            <div>
              <label className={labelClass}>Game</label>
              <input name="game" value={form.game} onChange={handleChange} placeholder="SOCOM II" className={inputClass} />
            </div>

            {/* Host */}
            <div>
              <label className={labelClass}>Hosted by</label>
              <input name="host" value={form.host} onChange={handleChange} placeholder="The Country Club" className={inputClass} />
            </div>

            {/* Date */}
            <div>
              <label className={labelClass}>Date *</label>
              <input name="date" type="date" value={form.date} onChange={handleChange} className={inputClass} required />
            </div>

            {/* Time */}
            <div>
              <label className={labelClass}>Time</label>
              <input name="time" type="time" value={form.time} onChange={handleChange} className={inputClass} />
            </div>

            {/* Max Players */}
            <div>
              <label className={labelClass}>Max Players</label>
              <input name="maxPlayers" type="number" value={form.maxPlayers} onChange={handleChange} placeholder="32" className={inputClass} />
            </div>

            {/* Discord Link */}
            <div>
              <label className={labelClass}>Discord / Signup Link</label>
              <input name="discordLink" value={form.discordLink} onChange={handleChange} placeholder="https://discord.gg/..." className={inputClass} />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} placeholder="Join us for an evening of classic PS2 online gaming..." rows={3} className={inputClass} />
            </div>

            {/* Rules */}
            <div className="md:col-span-2">
              <label className={labelClass}>Rules / Notes</label>
              <textarea name="rules" value={form.rules} onChange={handleChange} placeholder="- No glitches&#10;- Best of 11 rounds&#10;- Must be in Discord voice" rows={3} className={inputClass} />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm font-mono hover:bg-blue-500/30 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update Event' : 'Create Event'}
            </button>
            {editingId && (
              <button type="button" onClick={handleCancel} className="px-4 py-2.5 rounded-lg text-white/30 text-sm font-mono hover:text-white/60 transition-colors">
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Existing Events List */}
        <div>
          <h3 className="text-xs font-mono text-white/30 uppercase tracking-wider mb-4">
            All Events ({events.length})
          </h3>

          {loading ? (
            <div className="text-white/20 text-xs font-mono">Loading...</div>
          ) : events.length === 0 ? (
            <div className="text-white/15 text-xs font-mono py-8 text-center">No events created yet</div>
          ) : (
            <div className="space-y-2">
              {events.map(event => {
                const isPast = new Date(event.date) < new Date();
                return (
                  <div key={event.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isPast ? 'bg-white/[0.01] border-white/[0.03] opacity-50' : 'bg-white/[0.03] border-white/[0.06]'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/70 font-medium truncate">{event.title}</p>
                      <p className="text-[10px] font-mono text-white/25">
                        {event.date} {event.time && `· ${event.time}`} {event.game && `· ${event.game}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleEdit(event)} className="px-2.5 py-1 rounded text-[10px] font-mono text-blue-400/60 bg-blue-400/5 border border-blue-400/10 hover:text-blue-400 hover:bg-blue-400/10 transition-colors">
                        Edit
                      </button>
                      {confirmingId === event.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="px-2.5 py-1 rounded text-[10px] font-mono text-white bg-red-600 hover:bg-red-700 font-bold transition-colors cursor-pointer"
                          >
                            Confirm Delete?
                          </button>
                          <button
                            onClick={() => setConfirmingId(null)}
                            className="px-2 py-1 rounded text-[10px] font-mono text-white/40 hover:text-white/70 cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmingId(event.id)}
                          className="px-2.5 py-1 rounded text-[10px] font-mono text-red-400/60 bg-red-400/5 border border-red-400/10 hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
